import React from 'react';
import {Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, InputAdornment, IconButton, TextField} from '@mui/material';
import {Box} from '@mui/system';
import {visuallyHidden} from '@mui/utils';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {translate} from '../../../i18n/customI18nProvider';
import { getDeviceList } from '../../../services/device.service';
import StyledTableRow from '../common/table/StyledTableRow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DangerousIcon from '@mui/icons-material/Dangerous';
import HelpIcon from '@mui/icons-material/Help';
import config from '../../../config';
import { useHistory } from 'react-router-dom';

const tableHeader = [
    {
        id: 'type',
        key: 'type',
        align: 'left',
        disablePadding: false,
        label: translate('custom.type'),
        sortable: true
    },
    {
        id: 'number',
        key: 'number',
        align: 'left',
        disablePadding: false,
        label: translate('custom.number'),
        sortable: true
    },
    {
        id: 'app_version',
        key: 'app_version',
        align: 'left',
        disablePadding: false,
        label: translate('custom.app_version'),
        sortable: true
    },
    {
        id: 'latest_version',
        key: 'latest_version',
        align: 'left',
        disablePadding: false,
        label: translate('custom.latest_version'),
        sortable: true
    },
    {
        id: 'is_latest_version',
        key: 'is)latest_version',
        align: 'center',
        disablePadding: false,
        label: translate('custom.up_to_date'),
        sortable: true
    },
    {
        id: 'updated',
        key: 'updated',
        align: 'left',
        disablePadding: false,
        label: translate('custom.updated'),
        sortable: true
    },
    {
        id: 'actions',
        key: 'actions',
        align: 'left',  
        disablePadding: false,
        label: translate('custom.actions'),
        sortable: false
    }
]

class DeviceListComponent extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            devices: [],
            count: 0,
            skip: 0,
            limit: 50,
            limitOptions: [10, 50, 100, 250, 500],
            sort_key: '',
            sort_order: '',
            search: '',
            refresh_counter: props.refresh_counter ? props.refresh_counter : 0
        };
    }

    async componentDidMount() {
        this.getDevices();
    }

    async componentDidUpdate(prevProps) {
        if ((prevProps.refresh_counter !== this.props.refresh_counter)) {
            await this.setState({
                skip: 0
            });
            this.getDevices();
        }
    }

    async getDevices() {
        let options = {
            skip: this.state.skip,
            limit: this.state.limit
        }

        if (this.props.number && this.props.number.length > 0) {
            options.number = this.props.number.join(',');
        }
        
        if (this.props.type) {
            options.type = this.props.type;
        }

        if (this.props.outdated) {
            options.outdated = this.props.outdated;
        }
        
        if (this.state.sort_key) {
            options.sort_key = this.state.sort_key;
            options.sort_order = this.state.sort_order;
        }

        if (this.state.search) {
            options.search = this.state.search;
        }

        let devices = await getDeviceList(options);
        this.setState({
            devices: devices.devices,
            count: devices.count
        });
    }

    render() {

        return (
            <>

                {(this.props.hide_search) ? null : (
                    <Grid container style={{marginBottom: '10px'}}>
                        <Card style={{width: '100%', padding: '20px'}}>
                            <Grid item xs={12}>
                                <TextField
                                    id="search-input"
                                    size='small'
                                    label={translate('custom.search')}
                                    variant="outlined"
                                    fullWidth
                                    value={this.state.search}
                                    onChange={async (e) => {
                                        await this.setState({search: e.target.value});
                                        this.getDevices();
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        </Card>
                    </Grid>
                )}


                <TableContainer component={Card}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow>
                                {tableHeader.map((headCell) => {

                                    if (this.props.hide_columns && this.props.hide_columns.includes(headCell.key)) {
                                        return null;
                                    }

                                    return (
                                        <TableCell
                                            key={headCell.key}
                                            align={headCell.align ? headCell.align : 'left'}
                                            padding={headCell.disablePadding ? 'none' : 'normal'}
                                            sortDirection={(this.state.sort_key === headCell.key) ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : false}
                                        >
                                            {(headCell.sortable) ? (
                                                <TableSortLabel
                                                    active={this.state.sort_key === headCell.key}
                                                    direction={this.state.sort_key === headCell.key ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : 'asc'}
                                                    onClick={async () => {
                                                        await this.setState({
                                                            sort_key: headCell.key,
                                                            sort_order: this.state.sort_key === headCell.key ? (this.state.sort_order === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
                                                        })
                                                        this.getDevices();
                                                    }}
                                                >
                                                    {headCell.label}
                                                    {this.state.sort_key === headCell.key ? (
                                                        <Box component="span" sx={visuallyHidden}>
                                                            {this.state.sort_order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : (
                                                <>
                                                    {headCell.label}
                                                </>
                                            )}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.devices.map((row) => (
                                <StyledTableRow key={row._id} hover
                                    onDoubleClick={() => {
                                        if (this.props.hide_columns && this.props.hide_columns.includes('actions')) {
                                            return;
                                        }
                                        this.props.history.push('/v2/admin/devices/' + row._id + '/view');
                                    }}
                                >
                                    <TableCell>{translate('custom.' + row.type)}</TableCell>
                                    <TableCell>{row.number}</TableCell>
                                    <TableCell>{row.app_version}</TableCell>
                                    <TableCell title={row.latest_version ? "" : translate('custom.missing_latest_app_version')}>
                                        {row.type == config.device_types.SMARTPHONE.key ? (row.latest_version ?? (<a href="/admin#/latest_app"><HelpIcon color="warning" /></a>)) : '' }
                                    </TableCell>
                                    <TableCell align='center'>
                                        {row.type == config.device_types.SMARTPHONE.key ? (
                                            row.is_latest_version ? (
                                                <CheckCircleIcon color='success' />
                                            ) : (
                                                <DangerousIcon color='error' />
                                            )
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{row.updated}</TableCell>
                                    {(this.props.hide_columns && this.props.hide_columns.includes('actions')) ? null : (
                                        <TableCell>
                                            <IconButton
                                                size='small'
                                                onClick={() => {
                                                    this.props.history.push('/v2/admin/devices/' + row._id + '/view');
                                                }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid container style={{width: '100%'}}>
                    <Grid item xs={12}> 
                        <TablePagination
                            component="div"
                            count={this.state.count}
                            page={this.state.skip / this.state.limit}
                            onPageChange={async (event, page) => {
                                await this.setState({
                                    skip: page * this.state.limit
                                })
                                this.getDevices();
                            }}
                            rowsPerPage={this.state.limit}
                            onRowsPerPageChange={async (event) => {
                                await this.setState({
                                    limit: event.target.value
                                })
                                this.getDevices();
                            }}
                            rowsPerPageOptions={this.state.limitOptions}
                        />
                    </Grid>
                </Grid>
            </>
        )

    }

}

// wrap and export
const DeviceList = (props) => {

    const history = useHistory();

    return (
        <DeviceListComponent history={history} {...props} />
    );
};

export default DeviceList;